import { useEffect, useMemo, useState } from 'react'
import { Command } from 'cmdk'
import { useTranslation } from 'react-i18next'
import { LangBadge } from '@/components/ui/badge'
import { formatNumber, highlightMatch } from '@/lib/utils'
import { filterTerms, useTerms } from '@/hooks/use-term-data'
import type { Term } from '@/types/terms'

interface CommandPaletteProps {
  collectionId: number | null
  onSelect: (t: Term) => void
}

/**
 * ⌘K jump-to-term across the whole collection. The old UI had no way to reach a
 * specific term other than typing into the filter and hoping it fell inside the
 * 1000-row cap.
 */
export function CommandPalette({ collectionId, onSelect }: CommandPaletteProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const { data: terms } = useTerms(collectionId)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Cap the rendered result set — cmdk is not virtualised.
  const results = useMemo(() => {
    if (!terms) return []
    return filterTerms(
      terms,
      { q: query, lang: '', domain: '', abbrOnly: false, sort: 'freq' },
    ).slice(0, 50)
  }, [terms, query])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-[12vh]"
      onClick={() => setOpen(false)}
    >
      <Command
        shouldFilter={false}
        loop
        className="w-full max-w-xl overflow-hidden rounded-lg border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <Command.Input
          autoFocus
          value={query}
          onValueChange={setQuery}
          placeholder={t('termBase.searchTerms')}
          className="w-full border-b border-border bg-transparent px-4 py-3 text-sm outline-none placeholder:text-muted-foreground"
        />
        <Command.List className="scrollbar-thin max-h-80 overflow-y-auto p-1">
          <Command.Empty className="p-4 text-center text-sm text-muted-foreground">
            {t('common.noMatchesFound')}
          </Command.Empty>
          {results.map((term) => (
            <Command.Item
              key={term.term_id}
              value={String(term.term_id)}
              onSelect={() => {
                onSelect(term)
                setOpen(false)
                setQuery('')
              }}
              className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-sm data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
            >
              <LangBadge lang={term.lang} />
              <span className="flex-1 truncate">
                {highlightMatch(term.value, query).map((seg, i) =>
                  seg.match ? (
                    <mark
                      key={i}
                      className="rounded-sm bg-highlight px-0.5 text-highlight-foreground"
                    >
                      {seg.text}
                    </mark>
                  ) : (
                    <span key={i}>{seg.text}</span>
                  ),
                )}
              </span>
              <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                {formatNumber(term.freq)}×
              </span>
            </Command.Item>
          ))}
        </Command.List>
      </Command>
    </div>
  )
}
